import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    ApproversMissionApprovalDto,
    AttachmentHandler,
    calculateCombinedStatus,
    DisplayableMissionDto,
    DO_NOTHING,
    findActiveApprovals,
    FlyFreelyError,
    FlyFreelyLoggingService,
    hasFeatureFlag,
    MissionApprovalService,
    MissionApprovalUpdateCommand,
    MissionNoticeDto,
    MissionService,
    Organisation,
    PrintOption,
    RejectMissionApprovalCommand,
    RequestersMissionApprovalDto,
    RevokeMissionApprovalCommand,
    UserService,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { MODAL_OPTIONS } from '@flyfreely-portal-ui/ngx-bootstrap-customisation';
import { ApplyApprovalConditions } from 'libs/approval-conditions/src/lib/apply-approvals/apply-approval-conditions.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import {
    ApprovalScreenStatus,
    getSimplifiedAuthorityFromApproval
} from '../../helpers';
import { MissionApprovalWithAuthority } from '../../interfaces';
import { MissionEditService } from '../mission-edit.service';

const FEATURE_APPROVAL_CONDITIONS_LIBRARY = 'approvalConditionsLibrary';

@Component({
    selector: 'inline-mission-approval',
    templateUrl: './inline-mission-approval.component.html'
})
export class InlineMissionApprovalComponent implements OnInit, OnDestroy {
    @Input() organisation!: Organisation;
    @Input() mission!: DisplayableMissionDto;
    @Input() approval!: MissionApprovalWithAuthority;
    @Input() approvalNumber!: number;
    @Input() readonly: boolean = false;

    canUseApprovalConditionsLibrary = false;
    selectedAddConditions = false;
    isApprovalChecklistChanged = false;
    isMissionApprovable = false;

    isEditingAttachments = false;

    status: ApprovalScreenStatus;
    attachmentsHandler: AttachmentHandler;
    approval$ = new BehaviorSubject<ApproversMissionApprovalDto>(null);
    printOptions: PrintOption[] = [];

    getSimplifiedAuthorityFromApproval = getSimplifiedAuthorityFromApproval;

    working = false;
    private ngUnsubscribe$ = new Subject<void>();

    conditions = new FormArray([]);
    form = new FormGroup({
        conditions: this.conditions,
        messageFromApprover: new FormControl(undefined),
        approversNotes: new FormControl(undefined),
        maximumHeight: new FormControl(undefined)
    });

    constructor(
        private modalService: BsModalService,
        private logging: FlyFreelyLoggingService,
        private missionService: MissionService,
        private missionApprovalService: MissionApprovalService,
        private userService: UserService,
        private commonDialoguesService: CommonDialoguesService,
        private missionEditService: MissionEditService,
        private workTracker: WorkTracker
    ) {}

    ngOnInit() {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));

        this.missionApprovalService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.loadFullApproval());

        this.canUseApprovalConditionsLibrary = hasFeatureFlag(
            this.organisation,
            FEATURE_APPROVAL_CONDITIONS_LIBRARY
        );

        this.status = {
            readOnly: false,
            isPending: false,
            isBeingReviewed: false,
            canAttachFiles: false
        };

        if (this.mission?.id != null) {
            this.attachmentsHandler = this.missionService.attachmentHandler(
                this.mission.id,
                this.organisation.id
            );
            this.attachmentsHandler.destroyOn(this.ngUnsubscribe$);
        }

        this.loadFullApproval();
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.approval$.complete();
    }

    private loadFullApproval() {
        if (!this.hasApprove) {
            // Without approver access the status only view is shown, and
            // fetching the approver's view of the approval would be rejected.
            return;
        }
        const approvalId = this.approval.approval.id;

        this.missionApprovalService
            .findMissionApproval(approvalId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: fullApproval => this.applyFullApproval(fullApproval),
                error: error => {
                    this.logging.error(
                        error,
                        `Error loading approval: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    private applyFullApproval(fullApproval: ApproversMissionApprovalDto) {
        this.approval$.next(fullApproval);
        this.missionEditService.missionApprovalUpdated(fullApproval);
        this.form.patchValue({
            messageFromApprover: fullApproval.messageFromApprover,
            approversNotes: fullApproval.approversNotes,
            maximumHeight: fullApproval.maximumHeight
        });
        // Reset conditions FormArray before populating.
        while (this.conditions.length > 0) {
            this.conditions.removeAt(0);
        }
        fullApproval.conditions.forEach(c =>
            this.conditions.push(this.createCondition(c))
        );

        this.status = {
            isPending: false,
            canAttachFiles: false,
            isBeingReviewed: false,
            readOnly: false
        };

        if (fullApproval.status === 'PENDING') {
            this.status.isPending = true;
            this.status.canAttachFiles = true;
        } else if (fullApproval.status === 'BEING_REVIEWED') {
            this.status.isBeingReviewed = true;
            this.status.canAttachFiles = true;
        } else {
            // APPROVED, REJECTED, REVOKED, CANCELLED
            this.status.readOnly = true;
            this.status.canAttachFiles = this.mission.status !== 'FINALISED';
        }

        this.updateIsMissionApprovable();
    }

    /**
     * An approval can only be revoked once it has been granted, and the
     * mission has become ready to fly as a result.
     */
    get isMissionApprovalRevokeable() {
        if (this.approversApproval?.status !== 'APPROVED') {
            return false;
        }
        return (
            calculateCombinedStatus(
                this.mission.status,
                findActiveApprovals(this.mission.approvals, true).map(
                    a => a.status
                ),
                this.mission.readyForFinalisation ?? false
            ) === 'READY_TO_FLY'
        );
    }

    drop(event: CdkDragDrop<any[]>, array: any[]) {
        if (event.previousIndex !== event.currentIndex) {
            moveItemInArray(array, event.previousIndex, event.currentIndex);
        }
    }

    private createCondition(condition: string) {
        return new FormControl(condition);
    }

    deleteCondition(ix: number) {
        this.conditions.removeAt(ix);
    }

    addCondition() {
        this.conditions.push(this.createCondition(''));
    }

    addConditionsFromLibrary() {
        this.selectedAddConditions = true;

        const modal = this.modalService.show(ApplyApprovalConditions, {
            ...MODAL_OPTIONS,
            class: 'modal-task',
            initialState: {
                missionApprovalId: this.approval.approval.id
            }
        });

        modal.content.cancel
            .pipe(takeUntil(this.ngUnsubscribe$), takeUntil(modal.onHide))
            .subscribe({
                next: () => {
                    modal.hide();
                    this.selectedAddConditions = false;
                },
                error: DO_NOTHING
            });

        modal.content.appliedConditions
            .pipe(takeUntil(this.ngUnsubscribe$), takeUntil(modal.onHide))
            .subscribe({
                next: condition => {
                    this.conditionsSelected(condition);
                    modal.hide();
                },
                error: DO_NOTHING
            });
    }

    conditionsSelected(conditions: string[]) {
        conditions.forEach(condition =>
            this.conditions.push(this.createCondition(condition))
        );
        this.selectedAddConditions = false;
        this.logging.success(`Added ${conditions.length} conditions`);
    }

    markApprovalChecklistChanged() {
        this.isApprovalChecklistChanged = true;
        this.updateIsMissionApprovable();
    }

    updateIsMissionApprovable() {
        this.isMissionApprovable =
            this.approversApproval != null &&
            (
                this.approversApproval.mappedApproverChecks['pre-submit'] ?? []
            ).reduce(
                (acc, check) =>
                    acc &&
                    this.approversApproval.completedChecks.indexOf(check.id) !==
                        -1,
                true
            );
    }

    private buildUpdate(): MissionApprovalUpdateCommand {
        const values = this.form.value;
        return {
            completedChecks: this.approversApproval.completedChecks,
            conditions: values.conditions,
            approversNotes: values.approversNotes,
            messageFromApprover: values.messageFromApprover,
            maximumHeight: values.maximumHeight
        };
    }

    save() {
        this.missionApprovalService
            .updateApproval(this.approversApproval.id, this.buildUpdate())
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: result => {
                    this.logging.success('Approval saved');
                    this.approval$.next(result);
                    this.form.markAsPristine();
                    this.isApprovalChecklistChanged = false;
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error saving approval: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    startProgress() {
        const doneWorking = this.workTracker.createTracker();
        this.missionApprovalService
            .startReviewing(this.approversApproval.id)
            .then(
                () => {
                    this.status.isPending = false;
                    this.status.isBeingReviewed = true;
                    this.logging.info('The mission review is now started!');
                    doneWorking();
                },
                (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error starting progress: ${error.message}`
                    );
                    doneWorking();
                }
            );
    }

    stopProgress() {
        const doneWorking = this.workTracker.createTracker();

        this.missionApprovalService
            .stopReviewing(this.approversApproval.id)
            .then(
                () => {
                    this.status.isPending = true;
                    this.status.isBeingReviewed = false;
                    this.logging.info('The mission review is now stopped!');
                    doneWorking();
                },
                (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error stopping progress: ${error.message}`
                    );
                    doneWorking();
                }
            );
    }

    reject() {
        const values = this.form.value;
        const update: RejectMissionApprovalCommand = {
            approversNotes: values.approversNotes,
            message: values.messageFromApprover
        };
        const doneWorking = this.workTracker.createTracker();

        this.commonDialoguesService
            .showConfirmationDialogue(
                'Mission Approval',
                'Do you wish to reject this mission',
                'Reject',
                () =>
                    this.missionApprovalService
                        .rejectApproval(this.approversApproval.id, update)
                        .catch((error: FlyFreelyError) => {
                            this.logging.error(
                                error,
                                `Error while rejecting approval: ${error.message}`
                            );
                            throw error;
                        })
            )
            .then(
                () => {
                    this.logging.success(
                        `Approval ${this.approvalNumber} has been rejected`
                    );
                    this.form.markAsPristine();
                    this.isApprovalChecklistChanged = false;
                    this.loadFullApproval();
                    doneWorking();
                },
                () => doneWorking()
            );
    }

    private saveMissionIfChanged(): Promise<void> {
        const missionForm = this.missionEditService.missionForm;
        if (this.missionEditService.isReadOnly || missionForm.pristine) {
            return Promise.resolve();
        }
        return this.missionEditService
            .saveMission()
            .then(() => missionForm.markAsPristine())
            .catch((error: FlyFreelyError) => {
                this.logging.error(
                    error,
                    `Error saving the mission: ${error.message}`
                );
                return Promise.reject(error);
            });
    }

    approve() {
        const update = this.buildUpdate();
        const approvalId = this.approversApproval.id;
        const doneWorking = this.workTracker.createTracker();

        this.commonDialoguesService
            .showConfirmationDialogue(
                'Mission Approval',
                'Do you wish to approve this mission',
                'Approve',
                () =>
                    // The mission is locked once it has been approved, so any
                    // unsaved changes are saved before it is granted.
                    this.saveMissionIfChanged().then(() =>
                        this.missionApprovalService.approveApproval(
                            approvalId,
                            update
                        )
                    )
            )
            .then(
                (approval: ApproversMissionApprovalDto) => {
                    this.logging.success(
                        `Approval ${this.approvalNumber} has been approved`
                    );
                    this.form.markAsPristine();
                    this.isApprovalChecklistChanged = false;
                    // The approve call returns the approval as it now stands,
                    // so this screen doesn't need to fetch it again.
                    this.applyFullApproval(approval);
                    doneWorking();
                },
                (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while approving: ${error.message}`
                    );
                    doneWorking();
                }
            )
            .catch(() => doneWorking());
    }

    revoke() {
        const update: RevokeMissionApprovalCommand = {
            approversNotes: this.approversApproval.approversNotes ?? '',
            message: this.approversApproval.messageFromApprover ?? ''
        };

        const doneWorking = this.workTracker.createTracker();

        this.commonDialoguesService
            .showConfirmationDialogue(
                'Revoke Mission Approval',
                'Do you wish to revoke the approval for this mission?',
                'Revoke',
                () =>
                    this.missionApprovalService
                        .revokeApproval(this.approversApproval.id, update)
                        .catch((error: FlyFreelyError) => {
                            this.logging.error(
                                error,
                                `Error while revoking approval: ${error.message}`
                            );
                            throw error;
                        })
            )
            .then(
                () => {
                    this.logging.success(
                        `Approval ${this.approvalNumber} has been revoked`
                    );
                    this.form.markAsPristine();
                    this.isApprovalChecklistChanged = false;
                    this.loadFullApproval();
                    doneWorking();
                },
                () => doneWorking()
            );
    }

    get formConditions() {
        return this.form.get('conditions') as FormArray;
    }

    /**
     * Is the current user an approver for this approval, and so shown the
     * approver's view rather than the requester's read only view. This stays
     * true once the approval has been actioned, unlike `canApprove`, which
     * only reports whether it can be approved as it stands.
     */
    get hasApprove() {
        return this.approval.approval.availableActions?.hasApprove === true;
    }

    /**
     * The approver's outcome as recorded on the mission's copy of the approval,
     * which is all that the requester has access to.
     */
    get requestersApproval(): RequestersMissionApprovalDto {
        return this.approval.approval;
    }

    get requestersConditions(): string[] {
        return this.requestersApproval.conditions ?? [];
    }

    /**
     * Has the approval been granted, and so the approver's message, conditions
     * and maximum height apply to the mission.
     */
    get isApproved() {
        const status = this.requestersApproval.status;
        return (
            status === RequestersMissionApprovalDto.Status.APPROVED ||
            status === RequestersMissionApprovalDto.Status.ACCEPTED
        );
    }

    get approversApproval() {
        return this.approval$.getValue();
    }

    get isReadOnly() {
        return (this.status?.readOnly ?? false) || this.readonly;
    }

    get hasBlockingLotams() {
        return (
            this.mission?.missionNoticeList?.some(
                l => l.status === MissionNoticeDto.Status.BLOCK
            ) ?? false
        );
    }

    get allLotamsAcknowledgedByApprover() {
        if (!this.mission?.missionNoticeList?.length) return true;
        const currentUser = this.userService.getCurrentUser();
        const applicable = this.mission.missionNoticeList.filter(
            l => l.status === MissionNoticeDto.Status.APPLICABLE
        );
        return applicable.every(l =>
            l.acknowledgementList.some(al => al.personId === currentUser?.id)
        );
    }
}
